import React, { useEffect, useState } from 'react'
import { Box, Button, CircularProgress, FormControl, InputLabel, MenuItem, Skeleton, Stack, Typography } from '@mui/material'
import Select, { SelectChangeEvent } from '@mui/material/Select'

import { FullNodeProp, SlideMetaData } from '../../../utils/types'
import { get, post } from '../../../utils/http'
import { isPrivate } from '../../../utils/config'

interface PredictionFormProps {
    slideData: FullNodeProp,
    closePredictionForm: Function
}

interface FormData {
    [key: string]: any,
    tissue_type: string,
    slide_type: string,
    prediction_type: string,
    prediction_model: string,
    request_type: string
}

interface PredictionData {
    tissue_type: string,
    slide_type: string,
    prediction_type: string,
    model_type: string,
    is_default: boolean
}

const PredictionForm = ({ slideData, closePredictionForm }: PredictionFormProps) => {
    const [metaData, setMetaData] = useState<SlideMetaData | null | {}>(null)
    const [formData, setFormData] = useState<FormData>({
        tissue_type: '',
        slide_type: '',
        prediction_type: '',
        prediction_model: '',
        request_type: ''
    })
    const [predictionData, setPredictionData] = useState<PredictionData[]>([])
    const [tissueOptions, setTissueOptions] = useState<string[]>([])
    const [slideOptions, setSlideOptions] = useState<string[]>([])
    const [predictionOptions, setPredictionOptions] = useState<string[]>([])
    const [modelOptions, setModelOptions] = useState<PredictionData[]>([])
    const [enableBtn, setEnableBtn] = useState<boolean>(false)
    const [loading, setLoading] = useState<boolean>(true)
    const [submitting, setSubmitting] = useState<boolean>(false)

    const fetchData = async () => {
        try {
            const models = await get(`prediction-model`)
            const slide = await get(`Images/metadata/${slideData.file_id}`)
            setPredictionData(models)
            setFormData({...slide, prediction_type: '', request_type: ''})
            setMetaData(slide)
        } catch (err) {
            setMetaData({})
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [slideData]) // eslint-disable-line

    const getUnique = (key: keyof PredictionData, filter: Partial<PredictionData> = {}): string[] => {
        return Array.from(
            new Set(predictionData.filter(d =>
                Object.entries(filter).every(([k, v]) => d[k as keyof PredictionData] === v)
            ).map(d => String(d[key])))
        )
    }

    const makeDropdowns = () => {
        try {
            setTissueOptions(getUnique("tissue_type"))
            setSlideOptions(getUnique("slide_type", { tissue_type: formData.tissue_type }))
            setPredictionOptions(getUnique("prediction_type", { tissue_type: formData.tissue_type, slide_type: formData.slide_type }))
            setModelOptions(predictionData.filter(d => d.tissue_type === formData.tissue_type && d.slide_type === formData.slide_type && d.prediction_type === formData.prediction_type))
        } catch (err) {
            console.log(err)
        }
    }

    useEffect(() => {
        if (predictionData.length) makeDropdowns()
    }, [formData.tissue_type, formData.slide_type, formData.prediction_type, predictionData])    // eslint-disable-line

    useEffect(() => {
        // Auto-select the model
        if (formData.tissue_type && formData.slide_type && formData.prediction_type) {
            const defaultModel = modelOptions.find(d => d.is_default)
            if (defaultModel) setFormData(prevForm => ({...prevForm, prediction_model: defaultModel.model_type}))
            else if (modelOptions.length === 1) setFormData(prevForm => ({...prevForm, prediction_model: modelOptions[0].model_type}))
            else setFormData(prevForm => ({...prevForm, prediction_model: ''}))
        }
    }, [formData.tissue_type, formData.slide_type, formData.prediction_type, modelOptions])   // eslint-disable-line

    const handleInputChange = (e: SelectChangeEvent) => {
        if (e.target.name === 'tissue_type' && e.target.value !== 'prostate') setFormData(prevForm => ({ ...prevForm, slide_type: 'general' }))
        setFormData(prevForm => ({...prevForm, [e.target.name]: e.target.value}))
    }

    useEffect(() => {
        if (metaData && 'id' in metaData) {
            let valid = true
            for (let i of ['tissue_type', 'slide_type', 'prediction_type', 'request_type'])
                if (!formData[i] || (formData[i] && !formData[i].length)) valid = false
            setEnableBtn(valid)
        }
    }, [formData])  // eslint-disable-line

    const submitPrediction = async () => {
        setSubmitting(true)
        try {
            // FIx prediction_type value
            const payload = {...formData, prediction_type: formData.prediction_type.split(',')[0]}
            await post('Images/prediction', payload)
        } catch (err) {
            console.log(err)
        } finally {
            setSubmitting(false)
            closePredictionForm()
        }
    }

    return (
        <Box className='d-flex flex-column gap-4'>
            <Typography variant="h5" component="h1">Predictions</Typography>
            {loading
                ? <Stack spacing={1}>
                    {Array.from({length: 4}, (_, i) => i).map(i => (<Skeleton key={i} variant="rectangular" height={48} />))}
                </Stack>
                : (metaData && 'id' in metaData)
                    ? <>
                        <Box className='d-flex flex-column gap-3'>
                            <FormControl fullWidth>
                                <InputLabel>Tissue Type</InputLabel>
                                <Select name='tissue_type' value={formData.tissue_type} label="Tissue Type" onChange={handleInputChange}>
                                    {tissueOptions.map((i, index) => <MenuItem key={index} value={i}>{i}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Slide Type</InputLabel>
                                <Select name='slide_type' value={formData.slide_type} label="Slide Type" onChange={handleInputChange}>
                                    {slideOptions.map((i, index) => <MenuItem key={index} value={i}>{i}</MenuItem>)}
                                </Select>
                            </FormControl>
                            <FormControl fullWidth>
                                <InputLabel>Prediction Type</InputLabel>
                                <Select name='prediction_type' value={formData.prediction_type} label="Prediction Type" onChange={handleInputChange}>
                                    {predictionOptions.map((i, index) => <MenuItem key={index} value={i}>{i.split(',')[1]}</MenuItem>)}
                                </Select>
                            </FormControl>
                            {isPrivate && <FormControl fullWidth>
                                <InputLabel>Prediction Model</InputLabel>
                                <Select name='prediction_model' value={formData.prediction_model || ""} label="Prediction Model" onChange={handleInputChange}>
                                    {modelOptions.map((i, index) => <MenuItem key={index} value={i.model_type}>{i.model_type} {i.is_default ? "(Default)" : ""}</MenuItem>)}
                                </Select>
                            </FormControl>}
                            <FormControl fullWidth>
                                <InputLabel>Request Type</InputLabel>
                                <Select name='request_type' value={formData.request_type} label="Request Type" onChange={handleInputChange}>
                                    <MenuItem value='start'>Start</MenuItem>
                                    <MenuItem value='stop'>Stop</MenuItem>
                                </Select>
                            </FormControl>
                        </Box>
                        <Box className='d-flex justify-content-center gap-3'>
                            {submitting
                                ? <CircularProgress size={30} />
                                : <>
                                    <Button variant="outlined" onClick={() => closePredictionForm()}>Close</Button>
                                    <Button variant="contained" onClick={submitPrediction} disabled={!enableBtn}>Submit</Button>
                                </>
                            }
                        </Box>
                    </>
                    : <Typography>Data not found</Typography>
            }
        </Box>
    )
}

export default PredictionForm